import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';

const STATUS_META = {
  new:         { label: 'New',         bg: '#dbeafe', color: '#1e40af' },
  in_progress: { label: 'In Progress', bg: '#fef3c7', color: '#92400e' },
  resolved:    { label: 'Resolved',    bg: '#d1fae5', color: '#065f46' },
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export default function WebInquiries() {
  const [data, setData]         = useState({ data: [], total: 0, page: 1, limit: 20 });
  const [loading, setLoading]   = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch]     = useState('');
  const [page, setPage]         = useState(1);
  const [viewItem, setViewItem] = useState(null);
  const [convertModal, setConvertModal] = useState({ open: false, item: null });
  const [clients, setClients]   = useState([]);
  const [users, setUsers]       = useState([]);
  const [convertForm, setConvertForm] = useState({ client_id: '', assigned_to: '', type: 'other', notes: '' });
  const [converting, setConverting] = useState(false);
  const [convertErr, setConvertErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/web-inquiries', {
        params: { page, limit: 20, status: statusFilter || undefined, search: search || undefined },
      });
      setData(res);
    } finally { setLoading(false); }
  }, [page, statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/clients', { params: { limit: 500, approval_status: 'approved' } }).then(r => setClients(r.data.data || [])).catch(() => {});
    api.get('/users').then(r => setUsers(r.data.data || r.data || [])).catch(() => {});
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/web-inquiries/${id}/status`, { status });
      load();
      if (viewItem?.id === id) setViewItem(v => ({ ...v, status }));
    } catch { alert('Failed to update status.'); }
  };

  const openConvert = (item) => {
    setConvertModal({ open: true, item });
    setConvertForm({ client_id: '', assigned_to: '', type: 'other', notes: '' });
    setConvertErr('');
  };

  const handleConvert = async () => {
    if (!convertForm.client_id) { setConvertErr('Please select a client.'); return; }
    setConverting(true); setConvertErr('');
    try {
      const { data: res } = await api.post(`/web-inquiries/${convertModal.item.id}/convert`, convertForm);
      setConvertModal({ open: false, item: null });
      setViewItem(null);
      load();
      alert(`✓ Created CRM inquiry ${res.inquiry_number}`);
    } catch (e) {
      setConvertErr(e.response?.data?.message || 'Conversion failed.');
    } finally { setConverting(false); }
  };

  const counts = data.data.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Web Inquiries</h1>
          <p className="page-subtitle">Submissions from the public contact form</p>
        </div>
      </div>

      {/* Status summary strip */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        {Object.entries(STATUS_META).map(([key, meta]) => (
          <div key={key} onClick={() => { setStatusFilter(statusFilter === key ? '' : key); setPage(1); }}
            style={{ flex: 1, padding: '10px 14px', background: statusFilter === key ? meta.bg : '#fff', border: `1px solid ${statusFilter === key ? meta.color : '#e2e8f0'}`, borderRadius: 10, cursor: 'pointer', textAlign: 'center' }}>
            <div style={{ fontSize: '1.3rem', fontWeight: 800, color: meta.color }}>{counts[key] || 0}</div>
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', marginTop: 2 }}>{meta.label}</div>
          </div>
        ))}
      </div>

      {/* Search + filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '12px 16px' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div className="search-input-wrap" style={{ flex: 1 }}>
              <span className="search-icon">
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </span>
              <input className="form-control" placeholder="Search name, email, subject, company…"
                value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="form-control form-select" style={{ width: 150 }} value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            {(search || statusFilter) && (
              <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setStatusFilter(''); setPage(1); }}>Clear</button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Ref #</th>
                <th>Name / Company</th>
                <th>Email</th>
                <th>Subject</th>
                <th>Product Interest</th>
                <th>Status</th>
                <th>Received</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8}><div className="loading-overlay"><div className="spinner" /></div></td></tr>}
              {!loading && data.data.length === 0 && (
                <tr><td colSpan={8}><div className="empty-state"><p>No web inquiries found.</p></div></td></tr>
              )}
              {!loading && data.data.map(row => {
                const sm = STATUS_META[row.status] || STATUS_META.new;
                const isConverted = !!row.converted_inquiry_id;
                return (
                  <tr key={row.id}>
                    <td style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      {row.inquiry_number}
                      {isConverted && <div style={{ fontSize: '0.65rem', color: '#059669', fontWeight: 700 }}>Converted</div>}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{row.name}</div>
                      {row.company && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{row.company}</div>}
                    </td>
                    <td style={{ fontSize: '0.82rem' }}>
                      <a href={`mailto:${row.email}`} style={{ color: 'var(--primary)', textDecoration: 'none' }}>{row.email}</a>
                      {row.phone && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{row.phone}</div>}
                    </td>
                    <td style={{ maxWidth: 180 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.83rem' }}>{row.subject}</div>
                    </td>
                    <td style={{ maxWidth: 140 }}>
                      {row.product_interest
                        ? <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.78rem', color: '#64748b' }}>{row.product_interest}</div>
                        : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    <td>
                      <span style={{ padding: '3px 10px', borderRadius: 12, background: sm.bg, color: sm.color, fontSize: '0.72rem', fontWeight: 700 }}>{sm.label}</span>
                    </td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDate(row.created_at)}</td>
                    <td>
                      <div className="table-actions">
                        <button className="btn btn-ghost btn-icon" data-tooltip="View" onClick={() => setViewItem(row)}>
                          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        </button>
                        {row.status === 'new' && (
                          <button className="btn btn-ghost btn-icon" data-tooltip="Mark In Progress"
                            style={{ color: '#d97706' }} onClick={() => updateStatus(row.id, 'in_progress')}>
                            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                          </button>
                        )}
                        {!isConverted && row.status !== 'resolved' && (
                          <button className="btn btn-ghost btn-icon" data-tooltip="Convert to CRM Inquiry"
                            style={{ color: '#059669' }} onClick={() => openConvert(row)}>
                            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>
                          </button>
                        )}
                        {isConverted && row.crm_inquiry_number && (
                          <span style={{ fontSize: '0.7rem', color: '#059669', fontWeight: 700, padding: '2px 6px', background: '#d1fae5', borderRadius: 4 }}>
                            {row.crm_inquiry_number}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination page={data.page} total={data.total} limit={data.limit} onPageChange={setPage} />
      </div>

      {/* View Modal */}
      {viewItem && (
        <Modal open onClose={() => setViewItem(null)} title={`Web Inquiry — ${viewItem.inquiry_number}`} size="lg"
          footer={
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', width: '100%' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                {viewItem.status === 'new' && (
                  <button className="btn btn-secondary" onClick={() => updateStatus(viewItem.id, 'in_progress')}>
                    Mark In Progress
                  </button>
                )}
                {viewItem.status !== 'resolved' && !viewItem.converted_inquiry_id && (
                  <button className="btn btn-primary" onClick={() => { setViewItem(null); openConvert(viewItem); }}>
                    Convert to CRM Inquiry
                  </button>
                )}
                {viewItem.status !== 'resolved' && (
                  <button className="btn btn-secondary" onClick={() => updateStatus(viewItem.id, 'resolved')}>
                    Mark Resolved
                  </button>
                )}
              </div>
              <button className="btn btn-ghost" onClick={() => setViewItem(null)}>Close</button>
            </div>
          }
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginBottom: 16 }}>
            {[
              ['Name',    viewItem.name],
              ['Company', viewItem.company || '—'],
              ['Email',   <a key="e" href={`mailto:${viewItem.email}`}>{viewItem.email}</a>],
              ['Phone',   viewItem.phone || '—'],
              ['Status',  <span key="s" style={{ padding: '2px 10px', borderRadius: 12, background: STATUS_META[viewItem.status]?.bg, color: STATUS_META[viewItem.status]?.color, fontSize: '0.78rem', fontWeight: 700 }}>{STATUS_META[viewItem.status]?.label}</span>],
              ['Received', fmtDate(viewItem.created_at)],
            ].map(([label, val]) => (
              <div key={label}>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: '0.88rem', color: 'var(--text-primary)' }}>{val}</div>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Subject</div>
            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{viewItem.subject}</div>
          </div>

          {viewItem.product_interest && (
            <div style={{ marginBottom: 12, padding: '8px 12px', background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', marginBottom: 2 }}>Product of Interest</div>
              <div style={{ fontSize: '0.85rem', color: '#1e3a8a' }}>{viewItem.product_interest}</div>
            </div>
          )}

          <div style={{ marginBottom: viewItem.notes ? 12 : 0 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Message</div>
            <div style={{ fontSize: '0.85rem', background: 'var(--bg-secondary)', padding: '10px 14px', borderRadius: 8, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {viewItem.message || '—'}
            </div>
          </div>

          {viewItem.notes && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Internal Notes</div>
              <div style={{ fontSize: '0.85rem', background: '#fefce8', padding: '10px 14px', borderRadius: 8, color: '#713f12', whiteSpace: 'pre-wrap', border: '1px solid #fde68a' }}>
                {viewItem.notes}
              </div>
            </div>
          )}

          {viewItem.crm_inquiry_number && (
            <div style={{ marginTop: 12, padding: '8px 12px', background: '#d1fae5', borderRadius: 8, fontSize: '0.82rem', color: '#065f46', fontWeight: 600 }}>
              Converted to CRM inquiry: {viewItem.crm_inquiry_number}
            </div>
          )}
        </Modal>
      )}

      {/* Convert Modal */}
      <Modal open={convertModal.open} onClose={() => setConvertModal({ open: false, item: null })}
        title="Convert to CRM Inquiry" size="md"
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setConvertModal({ open: false, item: null })} disabled={converting}>Cancel</button>
            <button className="btn btn-primary" onClick={handleConvert} disabled={converting}>
              {converting ? 'Converting…' : 'Create CRM Inquiry'}
            </button>
          </div>
        }
      >
        {convertModal.item && (
          <div>
            <div style={{ padding: '10px 14px', background: '#eff6ff', borderRadius: 8, marginBottom: 16, fontSize: '0.83rem', color: '#1e40af' }}>
              <strong>{convertModal.item.name}</strong> — {convertModal.item.subject}
            </div>
            {convertErr && <div className="alert alert-error" style={{ marginBottom: 12 }}>{convertErr}</div>}
            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <label className="form-label">Link to Client *</label>
                <select className="form-control form-select" value={convertForm.client_id}
                  onChange={e => setConvertForm(f => ({ ...f, client_id: e.target.value }))}>
                  <option value="">Select client…</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 4 }}>
                  Can't find the client? Create them in Clients first, then come back.
                </div>
              </div>
              <div>
                <label className="form-label">Assign To</label>
                <select className="form-control form-select" value={convertForm.assigned_to}
                  onChange={e => setConvertForm(f => ({ ...f, assigned_to: e.target.value }))}>
                  <option value="">Unassigned</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Inquiry Type</label>
                <select className="form-control form-select" value={convertForm.type}
                  onChange={e => setConvertForm(f => ({ ...f, type: e.target.value }))}>
                  <option value="product">Product</option>
                  <option value="cro_service">CRO Service</option>
                  <option value="custom_synthesis">Custom Synthesis</option>
                  <option value="method_development">Method Development</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="form-label">Notes (internal)</label>
                <textarea className="form-control" rows={2} value={convertForm.notes}
                  onChange={e => setConvertForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Optional notes for the CRM inquiry…" />
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
